// @ts-check
/* global Tygh $ module require*/
'use strict';

declare var $;

import { ConfiguratorV2 } from '../configurator_v2';
import { ProductPreviewUploader } from './product_preview_uploader';
import type { OfflineConfiguration } from '../../tv_admin/tv_configurator_v2/offline_configuration';

export type TextWidths = {
    total: number;
    char: number;
    buffer: number;
    leading: number;
    combined: number;
};

/**
 * Class to handle in-browser product preview.
 */
export class ProductPreview {
    canvas: HTMLCanvasElement;
    canvas_dimensions: {height: number, width: number, offset_x: number, offset_y: number} = {
        height: 600, width: 600, offset_x: 0, offset_y: 0
    };
    configurator_v2: ConfiguratorV2;
    current_font_size: number;
    elements: {[key: string]: HTMLElement} = {};
    ignored_styles: string[] = ['d', 'x', 'y', 'transform', 'id', 'xml:space', 'clip-path', 'style'];
    initialized: boolean;
    offline_configuration: OfflineConfiguration;
    preview_type: string;
    template_svg: Document;
    uploader: ProductPreviewUploader;
    load_bar_color: string = "green";
    load_bar_height: number = 20;
    load_bar_offset: number = 10;
    loading_photo: boolean;
    move_start_position: {x: number, y: number};
    moving_photo: boolean;
    photo: HTMLImageElement;
    photo_path: string;
    photo_position: {x: number, y: number, scale_x: number, scale_y: number, rotation: number};
    photo_upload_area: {min_size: number, max_size: number} = {min_size: 1, max_size: 1};
    photo_upload_element: Element;
    photo_upload_placeholder: Element;
    photo_upload_required: boolean = false;
    print_size: {width_scale: number, height_scale: number, min: number, max: number} = {
        width_scale: 1, height_scale: 1, min: 1, max: 1
    };
    resize_observer : ResizeObserver;
    rotation_amount = 90;
    scale_slider: HTMLInputElement;
    scale_step: number = 0.1;
    smart_code: string;
    svg_images: {[key: string]: HTMLImageElement} = {};
    template_scale: number = 1;
    use_bg_image: boolean = false;
    window_scale: number = 1;
    // For testing
    show_text_curve_lines: boolean = false;

    constructor(configurator_v2) {
        this.configurator_v2 = configurator_v2;
        this.init_canvas();
        this.bind();
    }

    /**
     * Attach events and map references to the DOM.
     *
     * @returns {void}
     */
    bind() : void {
        // Scale canvas with window.
        this.resize_observer = new ResizeObserver(this.scale_canvas.bind(this));
        this.resize_observer.observe(this.canvas.parentElement.parentElement);
        const add_to_cart_button = document.getElementById(`button_cart_${this.configurator_v2.product_id}`);
        add_to_cart_button.addEventListener('click', this.update_canvas.bind(this, true));

        if (this.preview_type === "photo_upload" || this.preview_type === "photo_upload_bg") {
            this.scale_slider = document.getElementById('js-photo-upload-scale-slider') as HTMLInputElement;
            this.scale_slider.addEventListener('input', this.update_canvas.bind(this, false));
            this.elements.increase_scale_button = document.getElementById('js-photo-upload-increase-scale');
            this.elements.increase_scale_button.addEventListener('click', this.increase_photo_scale.bind(this));
            this.elements.decrease_scale_button = document.getElementById('js-photo-upload-decrease-scale');
            this.elements.decrease_scale_button.addEventListener('click', this.decrease_photo_scale.bind(this));
            this.elements.rotate_button = document.getElementById('js-photo-upload-rotate');
            this.elements.rotate_button.addEventListener('click', this.rotate_photo.bind(this));
            this.canvas.addEventListener('mousedown', this.mouse_down.bind(this)); 
            this.canvas.addEventListener('mousemove', this.mouse_move.bind(this));
            this.canvas.addEventListener('mouseup', this.mouse_stop.bind(this)); 
            this.canvas.addEventListener('mouseout', this.mouse_stop.bind(this));
            // Bind touch events for mobile.
            this.canvas.addEventListener('touchstart', this.touch_down.bind(this)); 
            this.canvas.addEventListener('touchmove', this.touch_move.bind(this));
            this.canvas.addEventListener('touchend', this.touch_stop.bind(this)); 
            this.canvas.addEventListener('touchcancel', this.touch_stop.bind(this));
            // Bind cta button.
            const cta_upload_button = document.getElementById('js-photo-preview-upload-cta');
            cta_upload_button.addEventListener('click', () => this.uploader.elements.choose_file_btn.click());

            if (this.preview_type === "photo_upload_bg") {
                const upload_offset_element = document.getElementById('preview_photo_upload_offset') as HTMLInputElement;
                const offset_values = JSON.parse(upload_offset_element.value);
                const upload_size_element = document.getElementById('preview_photo_upload_size') as HTMLInputElement;
                const size_values = JSON.parse(upload_size_element.value);
                const template_size_element = document.getElementById('preview_template_size') as HTMLInputElement;
                const template_size_values = JSON.parse(template_size_element.value);
                this.canvas_dimensions.offset_x = offset_values['x'];
                this.canvas_dimensions.offset_y = offset_values['y'];
                this.photo_upload_area.min_size = size_values['min'];
                this.photo_upload_area.max_size = size_values['max'];
                this.print_size.min = template_size_values['min'];
                this.print_size.max = template_size_values['max'];
            }
            
        }
    }

    mouse_down(e: MouseEvent) {
        this.start_moving_photo(e.clientX, e.clientY, e);
    }

    touch_down(e: TouchEvent) {
        this.start_moving_photo(e.touches[0].clientX, e.touches[0].clientY, e);
    }

    /**
     * Initiates photo movement if click is within bounds of photo.
     *
     * @param {number} x position of click.
     * @param {number} y position of click.
     * @param {MouseEvent | TouchEvent} e click event.
     * @returns {void}
     */
    start_moving_photo(x: number, y: number, e: MouseEvent | TouchEvent) {
        if(!this.photo || this.loading_photo) {
            return;
        }

        const bounding = this.canvas.getBoundingClientRect();
        x -= bounding.left;
        y -= bounding.top;
    
        const photo_width = this.photo.width * this.photo_position.scale_x *
            parseFloat(this.scale_slider.value) / this.window_scale;
        const photo_height = this.photo.height * this.photo_position.scale_y *
            parseFloat(this.scale_slider.value) / this.window_scale;
        let photo_position_x = this.photo_position.x / this.window_scale;
        let photo_position_y = this.photo_position.y / this.window_scale;
        let photo_max_x = photo_position_x + photo_width;
        let photo_max_y = photo_position_y + photo_height;

        // If photo is rotated, adjust dimensions.
        if (this.photo_position.rotation == 90 || this.photo_position.rotation == 270) {
            const width_dif = (photo_width - photo_height) / 2;
            photo_position_x = (this.photo_position.x + width_dif) / this.window_scale;
            photo_position_y = (this.photo_position.y - width_dif) / this.window_scale;
            photo_max_x = photo_position_x + photo_height;
            photo_max_y = photo_position_y + photo_width;
        }
    
        if (x >= photo_position_x &&
            x <= photo_max_x &&
            y >= photo_position_y &&
            y <= photo_max_y) {
            // Prevent owl carousel scrolling.
            e.stopPropagation();
            e.preventDefault();
            this.moving_photo = true;
            this.move_start_position = {x: x, y: y};
        }
    }

    mouse_move(e: MouseEvent) {
        this.move_photo(e.clientX, e.clientY, e);
    }

    touch_move(e: TouchEvent) {
        this.move_photo(e.touches[0].clientX, e.touches[0].clientY, e);
    }

    /**
     * Process photo movement and update canvas.
     *
     * @param {number} x position of event.
     * @param {number} y position of event.
     * @param {MouseEvent | TouchEvent} e move event.
     * @returns {void}
     */
    move_photo(x: number, y: number, e: MouseEvent | TouchEvent) {
        if (this.moving_photo) {
            const bounding = this.canvas.getBoundingClientRect();
            x -= bounding.left;
            y -= bounding.top;
            const dx = x - this.move_start_position.x;
            const dy = y - this.move_start_position.y;
            this.photo_position.x += dx;
            this.photo_position.y += dy;
            this.move_start_position = {x: x, y: y};
            window.requestAnimationFrame(this.update_canvas.bind(this, false));
        }
    }

    mouse_stop(e: MouseEvent) {
        this.stop_moving_photo(e.clientX, e.clientY, e);
    }

    touch_stop(e: TouchEvent) {
        this.stop_moving_photo(e.changedTouches[0].clientX, e.changedTouches[0].clientY, e);
    }

    /**
     * Terminates photo movement.
     *
     * @param {number} x position of event.
     * @param {number} y position of event.
     * @param {MouseEvent | TouchEvent} e end event.
     * @returns {void}
     */
    stop_moving_photo(x: number, y: number, e: MouseEvent | TouchEvent) {
        if (this.moving_photo) {
            this.move_photo(x, y, e);
            this.moving_photo = false;
        }
    }

    /**
     * Increases scale of photo then updates canvas.
     *
     * @returns {void}
     */
    increase_photo_scale() {
        this.scale_slider.value = (this.scale_slider.valueAsNumber + this.scale_step).toString();
        this.update_canvas();
    }

    /**
     * Decreases scale of photo then updates canvas.
     *
     * @returns {void}
     */
    decrease_photo_scale() {
        this.scale_slider.value = (this.scale_slider.valueAsNumber - this.scale_step).toString();
        this.update_canvas();
    }

     /**
     * Rotates photo then updates canvas.
     *
     * @returns {void}
     */
    rotate_photo() {
        if (!this.photo) {
            return;
        }

        let rotation = this.photo_position.rotation + this.rotation_amount;
        if (rotation > 360) {
            rotation -= 360;
        } else if (rotation < 0) {
            rotation += 360;
        }
        this.photo_position.rotation = rotation;
        this.update_canvas();
    }

    /**
     * Initialize canvas.
     *
     * @returns {void}
     */
    init_canvas() {
        const canvas = document.getElementById('tv-preview-canvas') as HTMLCanvasElement;
        if (!canvas) {
            return;
        }
        this.canvas = canvas;
        this.preview_type = (document.getElementById('product_preview') as HTMLInputElement).value;
        this.photo_upload_required = this.preview_type === "photo_upload" || this.preview_type === "photo_upload_bg";
        this.elements.canvas_container = document.getElementById('product-preview-container');
        this.initialized = false;
        this.draw_loading(this.canvas.getContext('2d'));
        
        // Get template svg.
        if (this.preview_type === "photo_upload" || this.preview_type === "photo_upload_bg") {
            this.use_bg_image = this.preview_type === "photo_upload_bg";
            this.uploader = new ProductPreviewUploader(this);
            this.uploader.init_uploader();
            this.elements.photo_upload_button = document.getElementById('js-btnPickfiles-photo-upload');
            this.elements.upload_photo_controls = document.getElementById('js-preview-upload-photo-controls');
            this.elements.adjust_photo_controls = document.getElementById('js-preview-adjust-photo-controls');
        }
        
        this.fetch_base_template(); 
    }

    /**
     * Scales canvas based on window dimensions then updates canvas.
     *
     * @returns {void}
     */
    scale_canvas() {
        if (!this.canvas) {
            return;
        }

        let max_width = window.getComputedStyle(this.canvas.parentElement, null).maxWidth;
        // Remove 'px' from widths before parsing as int.
        max_width = max_width.slice(0, max_width.length - 2);
        let container_width = this.canvas.parentElement.parentElement.style.width;
        container_width = container_width.slice(0, container_width.length - 2);
        const scaled_size = Math.min(parseInt(max_width), parseInt(container_width));
        this.window_scale = this.canvas_dimensions.width / scaled_size;
        this.canvas.setAttribute('width', scaled_size.toString());
        this.canvas.setAttribute('height', scaled_size.toString());
        this.canvas.parentElement.parentElement.parentElement.style.height = `${scaled_size}px`;
        this.update_canvas(false);
    }

    /**
     * Fetches base template for product.
     *
     * @returns {void}
     */
    fetch_base_template() {
        const path = this.offline_configuration ? location.pathname.substring(1) : 'index.php';
        if (!this.smart_code) {
            const smart_codes = this.configurator_v2.get_unique_smart_codes();
            this.smart_code = smart_codes[this.configurator_v2.parent_child_relation_ids[0]][0];
        }

        $.ceAjax('request', `${path}?dispatch=tv_configurator_v2.init_canvas`, {
            method: 'POST',
            caching: true,
            hidden: true,
            data: {
                smart_code: this.smart_code,
                configuration_id: this.configurator_v2.configuration_id,
                product_id: this.configurator_v2.product_id
            },
            callback: (response) => {
                if (response.results) {
                    this.fetch_template_callback(response);
                }
            }
        });
    }

    /**
     * Processes fetched template and updates canvas.
     *
     * @param {object} response from template retrieval. 
     * @returns {void}
     */
    fetch_template_callback(response) {
        const template_string = `<?xml version="1.0" encoding="UTF-8" standalone="no"?> ${response.results.template_data.svg}`;
        // Parse template.
        const parser = new DOMParser();
        this.template_svg = parser.parseFromString(template_string, "image/svg+xml");
        // The element which will contain the uploaded photo must have the id 'photo-upload-element'.
        this.photo_upload_element = this.template_svg.getElementById('photo-upload-element') as Element;
        this.photo_upload_placeholder = this.template_svg.getElementById('photo-upload-placeholder') as Element;

        let width_scale = 1;
        let height_scale = 1;
        let template_width = parseFloat(this.template_svg.documentElement.getAttribute('width'));
        let template_height = parseFloat(this.template_svg.documentElement.getAttribute('height'));
        const scaleX = this.canvas_dimensions.width / template_width;
        const scaleY = this.canvas_dimensions.height / template_height;
        this.template_scale = Math.min(scaleX, scaleY);

        if (this.use_bg_image) {
            // Set BG image to product sku image.
            const child_id = this.configurator_v2.parent_child_relation_ids[0];
            const bg_img = this.configurator_v2.child_configs[child_id].product_sku_image.src;
            this.elements.canvas_container.style.backgroundImage = `url('${bg_img}')`;
            // Scale canvas print canvas based on template data dimensions.
            const size_range = this.print_size.max - this.print_size.min;
            template_width = parseFloat(this.photo_upload_element.getAttribute('width'));
            template_height = parseFloat(this.photo_upload_element.getAttribute('height'));
            const largest_dimension = Math.max(template_width, template_height);
            const shortest_dimension = Math.min(template_width, template_height);
            const area_size = this.photo_upload_area.max_size - this.photo_upload_area.min_size;
            const percent_template = (largest_dimension - this.print_size.min) / size_range;
            const scaled_size = percent_template * area_size + this.photo_upload_area.min_size;
            const largest_scale = scaled_size / area_size
            const smallest_scale = scaled_size / largest_dimension * shortest_dimension / area_size
            if (template_width == largest_dimension) {
                width_scale = largest_scale;
                height_scale = smallest_scale;
            }
            else {
                width_scale = smallest_scale;
                height_scale = largest_scale;
            }
        }
        else {
            const offset_x = this.canvas_dimensions.width - template_width * this.template_scale;
            this.canvas_dimensions.offset_x = Math.max(offset_x / 2, 0);
            const offset_y = this.canvas_dimensions.height - template_height * this.template_scale;
            this.canvas_dimensions.offset_y = Math.max(offset_y / 2, 0);
        }

        if (this.preview_type === "photo_upload" || this.preview_type === "photo_upload_bg") {
            this.elements.photo_upload_button.classList.remove('tv-disabled');
            this.print_size.width_scale = width_scale;
            this.print_size.height_scale = height_scale;
            this.photo_position = {
                x: this.canvas_dimensions.offset_x,
                y: this.canvas_dimensions.offset_y,
                scale_x: 0,
                scale_y: 0,
                rotation: 0
            };
            this.uploader.photo_upload_path = response.results.photo_upload_path;
            if (this.photo) {
                this.reset_photo_position();
            }
        }

        // Load Fonts.
        const styles = this.template_svg.getElementsByTagName('style');
        if (styles.length > 0) {
            let fonts = [];
            for(let style of styles) {
                if (!style.textContent) {
                    continue;
                }

                const sheet = new CSSStyleSheet();
                sheet.replaceSync(style.textContent);
                for (const cssRule of sheet.cssRules) {
                    const font_rule = cssRule as CSSFontFaceRule;
                    const font_family = font_rule.style.getPropertyValue('font-family');
                    if (!font_family) {
                        continue;
                    }

                    const font_src = font_rule.style.getPropertyValue('src');
                    fonts.push(
                        new FontFace(font_family, font_src)
                    );
                }
            }
            fonts.forEach(
                (font) => font.load().then(() => {
                    document.fonts.add(font);
                    this.update_canvas();
                })
            );
        }

        this.initialized = true;
        this.scale_canvas();
    }

    /**
     * Fetches new base template if smart code has changed.
     *
     * @param {object} smart_codes of updated configuration.
     * @returns {void}
     */
    reinitialize(smart_codes) {
        const key = Object.keys(smart_codes[this.configurator_v2.parent_child_relation_ids[0]])[0];
        const smart_code = smart_codes[this.configurator_v2.parent_child_relation_ids[0]][key];
        if (this.smart_code == smart_code) {
            return;
        }
        this.smart_code = smart_code;
        this.initialized = false;

        if (this.preview_type === "photo_upload" || this.preview_type === "photo_upload_bg") {
            this.elements.photo_upload_button.classList.add('tv-disabled');
        }

        this.fetch_base_template();
    }

    /**
     * Draws canvas and optionally outputs resulting svg.
     *
     * @param {boolean} update_svg if resulting svg should be output.
     * @returns {void}
     */
    update_canvas(update_svg: boolean = false) {
        if (!this.canvas || !this.initialized || !this.canvas.getContext) {
            return;
        }

        const ctx = this.canvas.getContext("2d");
        if (!update_svg) {
            ctx.reset();
            ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        }

        if (!this.use_bg_image) {
            // Render each element of template in order.
            for (const element of this.template_svg.children) {
                this.render_element(ctx, element as SVGElement, update_svg);
            }
        }
        else { 
            this.draw_photo_upload(ctx, update_svg);
        }
        
        if (update_svg) {
            this.create_svg();
        }
    }

    /**
     * Draws a placeholder while base template is being fetched.
     *
     * @param {CanvasRenderingContext2D} ctx rendering context of canvas.
     * @param {number} count of draw loading iterations.
     * @returns {void}
     */
    draw_loading(ctx: CanvasRenderingContext2D, count: number = 0) {
        if (this.initialized) {
            return;
        }

        ctx.reset();
        ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        ctx.fillStyle = "black";
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        const rect_width = this.canvas_dimensions.height / this.window_scale;
        const rect_height = this.canvas_dimensions.height / this.window_scale;
        const rect_x = rect_width / 5;
        const rect_y = rect_height /2;
        ctx.font = `${rect_width * 0.05}px arial`;
        let text = "Loading";
        if (count > 3) {
            count = 0;
        }

        for(let i = 0; i < count; i++) {
            text += '.';
        }
        ctx.fillText(text, rect_x, rect_y);
        setTimeout(this.draw_loading.bind(this), 700, ctx, ++count);
    }

    /**
     * Draws the uploaded photo or a placeholder.
     *
     * @param {CanvasRenderingContext2D} ctx rendering context of canvas.
     * @param {boolean} update_svg if resulting svg should be output.
     * @returns {void}
     */
    draw_photo_upload(ctx: CanvasRenderingContext2D, update_svg: boolean = false) {
        const question_ids = (this.photo_upload_element as SVGElement).dataset.questionIds ? 
            JSON.parse((this.photo_upload_element as SVGElement).dataset.questionIds) : {};

        const photo_upload_area = this.get_photo_upload_area(this.photo_upload_element);
        const rect_width = photo_upload_area.width / this.window_scale;
        const rect_height = photo_upload_area.height / this.window_scale;
        let rect_x = photo_upload_area.x / this.window_scale;
        let rect_y = photo_upload_area.y / this.window_scale;

        const styles = this.get_styles(this.photo_upload_element as SVGElement, '', {'fill':'none'});
        ctx.fillStyle = this.get_fill_color(styles, question_ids);

        if (this.use_bg_image) {
            // Draw shadow
            const shadow_offset = 5;
            ctx.shadowColor = 'gray';
            ctx.shadowBlur = 20;
            ctx.shadowOffsetX = shadow_offset;
            ctx.shadowOffsetY = shadow_offset;
            ctx.rect(rect_x, rect_y, rect_width, rect_height);
            if (styles['fill'] != 'none') {
                ctx.fill();
            }

            ctx.shadowColor = 'transparent';
            rect_x += rect_width/2;
            rect_y += rect_height/2;
        }

        // Draw user uploaded image.
        // Show loading bar while photo is being uploaded.
        if (this.loading_photo) {
            const load_bar_height = Math.min(rect_height * 0.2, this.load_bar_height) / this.window_scale;
            const load_bar_offset = Math.min(rect_width * 0.1, this.load_bar_offset) / this.window_scale;
            ctx.fillStyle = this.load_bar_color;
            ctx.strokeStyle = this.load_bar_color;
            ctx.strokeRect(
                rect_x - rect_width / 2 + load_bar_offset,
                rect_y - load_bar_height / 2,
                rect_width - load_bar_offset * 2,
                load_bar_height
            );
            ctx.fillRect(
                rect_x - rect_width / 2 + load_bar_offset,
                rect_y - load_bar_height / 2,
                Math.max((rect_width * this.uploader.photo_upload_percent / 100) - load_bar_offset * 2, 0),
                load_bar_height
            );
            return;
        }
        else if(!this.photo) {
            if (this.photo_upload_placeholder) {
                return;
            }
            // Show placeholder text if photo hasn't been uploaded and no placeholder image exists.
            ctx.fillStyle = styles['fill'] == 'none' || styles['fill'] == '#ffffff' ? "black" : "white";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.font = `${rect_width * 0.15}px arial`;
            const font_height = ctx.measureText("Upload").fontBoundingBoxDescent;
            ctx.fillText("Upload", rect_x, rect_y - font_height);
            ctx.fillText("Your Photo", rect_x, rect_y + font_height);
            return;
        }

        const photo_width = this.photo.width * this.photo_position.scale_x *
            this.scale_slider.valueAsNumber / this.window_scale;
        const photo_height = this.photo.height * this.photo_position.scale_y *
            this.scale_slider.valueAsNumber / this.window_scale;
        const photo_position_x = this.photo_position.x / this.window_scale;
        const photo_position_y = this.photo_position.y / this.window_scale;

        // Draw outline of image dimensions.
        if (this.moving_photo) {
            ctx.save();
            ctx.lineWidth = 2;
            ctx.translate(photo_position_x + photo_width/2, photo_position_y + photo_height/2);
            ctx.rotate(this.photo_position.rotation * Math.PI / 180);
            ctx.translate(-(photo_position_x + photo_width/2), -(photo_position_y + photo_height/2));
            ctx.strokeRect(photo_position_x, photo_position_y, photo_width, photo_height);
            ctx.restore();
        }
        // Create clipping area.
        if (!this.use_bg_image) {
            this.set_transform(this.photo_upload_element as SVGGraphicsElement, ctx);
            const path = this.element_to_path(this.photo_upload_element as SVGElement);
            ctx.clip(path);
            ctx.resetTransform();
        }
        else {
            ctx.clip();
        }

        // Draw Image, except on svg output.
        if (!update_svg) {
            ctx.translate(photo_position_x + photo_width/2, photo_position_y + photo_height/2);
            ctx.rotate(this.photo_position.rotation * Math.PI / 180);
            ctx.translate(-(photo_position_x + photo_width/2), -(photo_position_y + photo_height/2));
            ctx.drawImage(
                this.photo,
                photo_position_x,
                photo_position_y,
                photo_width,
                photo_height
            );
            return;
        }  

        // Add image to svg
        const photo_id = 'uploaded-photo'; 
        if (this.photo) {
            let scaled_width = photo_width / this.template_scale * this.window_scale;
            let scaled_height = photo_height / this.template_scale * this.window_scale;
            let scaled_x = (photo_position_x - this.canvas_dimensions.offset_x) / this.template_scale * this.window_scale;
            let scaled_y = (photo_position_y - this.canvas_dimensions.offset_y) / this.template_scale * this.window_scale;

            // If using bg image, create new base svg.
            if (this.use_bg_image) {
                // Remove all elements from svg.
                const svg_root_element = this.template_svg.documentElement.cloneNode(false);
                this.template_svg = new Document();
                this.template_svg.appendChild(svg_root_element);
                // Add outline to photo upload element.
                this.photo_upload_element.setAttribute('stroke', 'black');
                this.photo_upload_element.setAttribute('stroke-width', '1');
                this.photo_upload_element.setAttribute('paint-order', 'stroke');
                this.template_svg.documentElement.appendChild(this.photo_upload_element);
                // Scale using max/min numbers.
                const width_scale = rect_width / parseFloat(this.photo_upload_element.getAttribute('width'));
                scaled_width = photo_width / width_scale;
                const height_scale = rect_height / parseFloat(this.photo_upload_element.getAttribute('height'));
                scaled_height = photo_height / height_scale;
                scaled_x = (photo_position_x - photo_upload_area.x) / width_scale;
                scaled_x += parseFloat(this.photo_upload_element.getAttribute('x'));
                scaled_y = (photo_position_y - photo_upload_area.y) / height_scale;
                scaled_y += parseFloat(this.photo_upload_element.getAttribute('y'));
            }

            const center_x = scaled_x + scaled_width/2;
            const center_y = scaled_y + scaled_height/2;

            // Create SVG Image.
            const img_group = document.createElementNS('http://www.w3.org/2000/svg','g');
            const svg_img = document.createElementNS('http://www.w3.org/2000/svg','image');
            img_group.appendChild(svg_img);
            svg_img.setAttribute('height', `${scaled_height}`);;
            svg_img.setAttribute('width',`${scaled_width}`);
            svg_img.setAttribute('id', photo_id);
            svg_img.setAttributeNS('http://www.w3.org/1999/xlink','href', `${window.location.origin}/${this.photo_path}`);
            svg_img.setAttribute('x', `${scaled_x}`);
            svg_img.setAttribute('y', `${scaled_y}`);
            svg_img.setAttribute('transform-origin', `${center_x}px ${center_y}px`);
            svg_img.setAttribute('style', `rotate:${this.photo_position.rotation}deg;`);
            // Create clip path.
            const clip_id = 'uploaded-photo-clip'
            const clip_path = document.createElementNS('http://www.w3.org/2000/svg','clipPath');
            clip_path.append(this.photo_upload_element.cloneNode(true));
            clip_path.lastElementChild.setAttribute('id', `${clip_id}-path`)
            clip_path.setAttribute('id', clip_id);
            img_group.setAttribute('clip-path', `url(#${clip_id})`);
            this.photo_upload_element.insertAdjacentElement('afterend', img_group);
            this.template_svg.documentElement.append(clip_path);
        }
    }

    /**
     * Draws the element on the canvas.
     *
     * @param {CanvasRenderingContext2D} ctx rendering context of canvas.
     * @param {SVGElement} element to be rendered.
     * @param {boolean} update_svg if resulting svg should be output.
     * @returns {void}
     */
    render_element(ctx: CanvasRenderingContext2D, element: SVGElement, update_svg: boolean = false) {
        const tag_name = element.tagName;
        ctx.fillStyle = 'white';
        ctx.lineWidth = 0.5;
        ctx.strokeStyle = 'black';
        ctx.save();

        // Don't draw clip paths.
        if (element.nodeName === "clipPath" || (element.parentElement && element.parentElement.nodeName === "clipPath")) {
            return;
        }

        const question_ids = element.dataset.questionIds ? JSON.parse(element.dataset.questionIds) : {};
        ctx.beginPath();
        
        // Create clipping region. Include clipping regions applied to any parent elements.
        if (tag_name !== 'text' && tag_name !=='tspan') {
            let clip_element = element;
            while(clip_element && clip_element.nodeType == Node.ELEMENT_NODE) {
                this.create_clipping_region(clip_element, ctx);
                clip_element = clip_element.parentNode as SVGElement;
            }
        }

        // Set Transform (position, width).
        this.set_transform(element as SVGGraphicsElement, ctx);
        // Get path style.
        const styles = this.get_styles(element); 
        let path = null;
        // Apply filters. Include filters applied to any parent elements.
        let filter_element = element;
        while(filter_element && filter_element.nodeType == Node.ELEMENT_NODE) {
            this.apply_filters(filter_element, ctx);
            filter_element = filter_element.parentNode as SVGElement;
        }

        if (tag_name === 'text') {
            for (const child of element.children) {
                if (child.tagName !== 'tspan') {
                    continue;
                }

                this.render_text(ctx, element as SVGTextElement, child as SVGTSpanElement, update_svg);
            }
        }
        else if (tag_name === 'image') {
            this.render_image(ctx, element as SVGImageElement, update_svg);
        }
        else {
            path = this.element_to_path(element);
        }

        if (path) {
            ctx.strokeStyle = styles['stroke'];
            styles['fill'] = this.get_fill_color(styles, question_ids);
            ctx.fillStyle = styles['fill'];
            // Don't draw the element when creating the svg.
            if (update_svg) {
                this.update_element_styles(element, styles);
            }
            else {
                if (styles['fill'] != 'none') {
                    ctx.fill(path);
                }
                else if (styles['stroke-width'] != 0) {
                    ctx.lineWidth = styles['stroke-width'] ?? 1;
                    ctx.stroke(path);
                }
            }

        }

        ctx.restore();

        if (element == this.photo_upload_element) {
            ctx.save();
            this.draw_photo_upload(ctx, update_svg);
            ctx.restore();
            return;
        }

        // Render each child element.
        for (const child_element of element.children) {
            this.render_element(ctx, child_element as SVGElement, update_svg);
        }
    }

    /**
     * Creates a Path2D from an svg element which can then be drawn on the canvas.
     *
     * @param {SVGElement} element to be rendered.
     * @returns {Path2D} of the svg element.
     */
    element_to_path(element: SVGElement) {
        const tag_name = element.tagName;
        if (tag_name === 'path') {
            return this.create_path(element as SVGPathElement);
        }
        else if (tag_name === 'rect') {
            return this.create_rect(element as SVGRectElement);
        }
        else if (tag_name === 'circle') {
            return this.create_circle(element as SVGCircleElement);
        }
        else if (tag_name === 'ellipse') {
            return this.create_ellipse(element as SVGEllipseElement);
        }
        else if (tag_name === 'line') {
            return this.create_line(element as SVGLineElement);
        }
        else if (tag_name === 'polyline') {
            return this.create_polyline(element as SVGPolylineElement);
        }
        else if (tag_name === 'polygon') {
            return this.create_polygon(element as SVGPolygonElement);
        }

        return null;
    }

    /**
     * Creates a Path2D from an svg path element.
     *
     * @param {SVGPathElement} path to be rendered.
     * @returns {Path2D} of the svg element.
     */
    create_path(path: SVGPathElement) {
        const path_value = path.getAttribute('d');
        const path2d = new Path2D(path_value);
        return path2d;
    }

    /**
     * Creates a Path2D from an svg polygon element.
     *
     * @param {SVGPolygonElement} polygon to be rendered.
     * @returns {Path2D} of the svg element.
     */
    create_polygon(polygon: SVGPolygonElement) {
        const path2d = new Path2D();
        let points = polygon.getAttribute('points').split(' ');
        path2d.moveTo(parseFloat(points[0]), parseFloat(points[1]));
        for (let i = 2; i < points.length; i +=2 ) {
            path2d.lineTo(parseFloat(points[i]), parseFloat(points[i+1]));
        }
        path2d.closePath();
        return path2d;
    }

    /**
     * Creates a Path2D from an svg polyline element.
     *
     * @param {SVGPolylineElement} polyline to be rendered.
     * @returns {Path2D} of the svg element.
     */
    create_polyline(polyline: SVGPolylineElement) {
        const path2d = new Path2D();
        let points = polyline.getAttribute('points').split(' ');
        path2d.moveTo(parseFloat(points[0]), parseFloat(points[1]));
        for (let i = 2; i < points.length; i +=2 ) {
            path2d.lineTo(parseFloat(points[i]), parseFloat(points[i+1]));
        }
        return path2d;
    }

    /**
     * Creates a Path2D from an svg rect element.
     *
     * @param {SVGRectElement} rect to be rendered.
     * @returns {Path2D} of the svg element.
     */
    create_rect(rect: SVGRectElement) {
        // Don't render bleed path rect.
        if (rect.id === 'Bleed_Path') {
            return null;
        }
        const rect_width = parseFloat(rect.getAttribute('width'));
        const rect_height = parseFloat(rect.getAttribute('height'));
        const rect_x = parseFloat(rect.getAttribute('x'));
        const rect_y = parseFloat(rect.getAttribute('y'));
        const radius_x = rect.getAttribute('rx');
        const radius_y = rect.getAttribute('ry');
        const path2d = new Path2D();
        // If rx or ry attribute is present, create a rounded rect.
        if (radius_x || radius_y) {
            path2d.roundRect(
                rect_x,
                rect_y,
                rect_width,
                rect_height,
                {
                    x: (radius_x ? parseFloat(radius_x) : 0),
                    y: (radius_y ? parseFloat(radius_y) : 0)
                }
            );
        }
        else {
            path2d.rect(rect_x, rect_y, rect_width, rect_height);
        }
        return path2d;
    }

    /**
     * Creates a Path2D from an svg circle element.
     *
     * @param {SVGCircleElement} circle to be rendered.
     * @returns {Path2D} of the svg element.
     */
    create_circle(circle: SVGCircleElement) {
        const circle_radius = parseFloat(circle.getAttribute('r'));
        const circle_x = parseFloat(circle.getAttribute('cx')); 
        const circle_y = parseFloat(circle.getAttribute('cy')); 
        const path2d = new Path2D();
        path2d.arc(circle_x, circle_y, circle_radius, 0, Math.PI * 2);
        return path2d;
    }

    /**
     * Creates a Path2D from an svg ellipse element.
     *
     * @param {SVGEllipseElement} ellipse to be rendered.
     * @returns {Path2D} of the svg element.
     */
    create_ellipse(ellipse: SVGEllipseElement) {
        const ellipse_rx = parseFloat(ellipse.getAttribute('rx'));
        const ellipse_ry = parseFloat(ellipse.getAttribute('ry'));
        const ellipse_x = parseFloat(ellipse.getAttribute('cx')); 
        const ellipse_y = parseFloat(ellipse.getAttribute('cy')); 
        const path2d = new Path2D();
        path2d.ellipse(ellipse_x, ellipse_y, ellipse_rx, ellipse_ry, Math.PI * 2, 0, Math.PI * 2);
        return path2d;
    }

    /**
     * Creates a Path2D from an svg line element.
     *
     * @param {SVGLineElement} line to be rendered.
     * @returns {Path2D} of the svg element.
     */
    create_line(line: SVGLineElement) {
    const line_x1 = parseFloat(line.getAttribute('x1')); 
        const line_y1 = parseFloat(line.getAttribute('y1')); 
        const line_x2 = parseFloat(line.getAttribute('x2')); 
        const line_y2 = parseFloat(line.getAttribute('y2')); 
        const path2d = new Path2D();
        path2d.moveTo(line_x1, line_y1);
        path2d.lineTo(line_x2, line_y2);
        return path2d;
    }

    /**
     * Draws the image element on the canvas.
     *
     * @param {CanvasRenderingContext2D} ctx rendering context of canvas.
     * @param {SVGImageElement} image element to be rendered.
     * @param {boolean} update_svg if resulting svg should be output.
     * @returns {void}
     */
    render_image(ctx: CanvasRenderingContext2D, image: SVGImageElement, update_svg: boolean = false) {
        // Don't draw images when oon SVG output.
        if (update_svg) {
            return;
        }

        const width = parseFloat(image.getAttribute('width'));
        const height = parseFloat(image.getAttribute('height'));
        const x = parseFloat(image.getAttribute('x'));
        const y = parseFloat(image.getAttribute('y'));
        const new_image = this.svg_images[image.href.baseVal] ? this.svg_images[image.href.baseVal] : new Image(width, height);
        new_image.src = image.href.baseVal;
        if (new_image.complete) {
            ctx.drawImage(new_image, x ? x : 0, y ? y : 0, width, height);
            return;
        }

        if (this.svg_images[image.href.baseVal]) {
            return;
        }
        // Update canvas when image has finished loading.
        this.svg_images[image.href.baseVal] = new_image;
        new_image.onload = (event) => {
            this.update_canvas();
        };
    }

    /**
     * Draws the tspan child of the text element on the canvas.
     *
     * @param {CanvasRenderingContext2D} ctx rendering context of canvas.
     * @param {SVGTextElement} text parent element of the tspan to be rendered.
     * @param {SVGTSpanElement} tspan child element to be rendered.
     * @param {boolean} update_svg if resulting svg should be output.
     * @returns {void}
     */
    render_text(
        ctx: CanvasRenderingContext2D,
        text: SVGTextElement,
        tspan: SVGTSpanElement,
        update_svg: boolean = false
    ) {
        const question_ids = text.dataset.questionIds ? JSON.parse(text.dataset.questionIds) : {};
        // When exporting svg, remove any additional lines already present.
        if (update_svg) {
            for (let i = text.childElementCount - 1; i > 0; i--) {
                if (text.children[i].tagName === 'tspan') {
                    text.removeChild(text.children[i]);
                }
            }
        }
        const text_style = tspan.getAttribute('style');
        const styles = this.get_styles(text, text_style, {
            'font-weight': 'normal'
        });

        // Add px to font-size if not present.
        const font_size = styles['font-size'] as string;
        if (font_size) {
            const px_index = font_size.search('px');
            if (px_index <= 0) {
                this.current_font_size = parseFloat(font_size);
                styles['font-size'] = font_size + 'px';
            }
            else {
                this.current_font_size = parseFloat(font_size.slice(0, px_index));
            }
        }

        // Used to determine maximum width when scaling (uncurved) text later.
        const initial_text = tspan.innerHTML;
        tspan.innerHTML = this.get_text(tspan.innerHTML, question_ids, styles);

        // Set Transform (position, width)
        let transform_matrix = ['1','0','0','1','0','0'];
        const text_transform = text.getAttribute('transform');
        if (text_transform.startsWith("matrix")) {
            const matrix_string = text_transform.substring("matrix(".length, text_transform.length - 1);
            transform_matrix = matrix_string.split(',');
        }
        ctx.setTransform(
            parseFloat(transform_matrix[0]) * this.template_scale / this.window_scale,
            parseFloat(transform_matrix[1]),
            parseFloat(transform_matrix[2]),
            parseFloat(transform_matrix[3]) * this.template_scale / this.window_scale,
            (parseFloat(transform_matrix[4]) * this.template_scale + this.canvas_dimensions.offset_x)
                / this.window_scale,
            (parseFloat(transform_matrix[5]) * this.template_scale + this.canvas_dimensions.offset_y)
                / this.window_scale
        );

        if (text_transform.startsWith("translate")) {
            const translate_string = text_transform.substring("translate(".length, text_transform.length - 1);
            const transform_translate = translate_string.split(' ');
            ctx.translate(parseFloat(transform_translate[0]), parseFloat(transform_translate[1]));
        }

        // Set font/fill styles.
        ctx.font = `${styles['font-weight']} ${styles['font-size']} ${styles['font-family']}`;
        let letters = tspan.innerHTML;

        // For text to be centered, 'text-align:center' will have to be added to the template's tspan style attribute.
        ctx.textAlign = styles['text-align'] ? styles['text-align'] : "left";
        // For text baseline, 'text-baseline:middle' or 'alignment-baseline:central' will have to be added
        // to the template's tspan style attribute.
        ctx.textBaseline = styles['text-baseline'] ? styles['text-baseline'] :
            styles['alignment-baseline'] ? styles['alignment-baseline'] : "alphabetic";
        if (styles['alignment-baseline'] && styles['alignment-baseline'] == 'central') {
            ctx.textBaseline = 'middle';
        }
        ctx.lineWidth = 2;
        styles['fill'] = this.get_fill_color(styles, question_ids);
        ctx.fillStyle = styles['fill'];
        ctx.strokeStyle = styles['stroke'] ?? 'black';

        // Add styles for svg output.
        styles['paint-order'] = 'stroke';
        styles['stroke-width'] = '.85pt';

        // Get arc for curved text.
        if (text.dataset['path']) {
            const path = JSON.parse(text.dataset.path);

            // Calculate leading space to correctly space and center text along path.
            const max_width = this.get_bezier_length(path);
            let widths = this.calculate_text_widths(ctx, letters, max_width);
            
            // Resize font if total_width + buffer_space > max_width.
            if (widths['combined'] > max_width) {
                this.scale_font_size(ctx, styles, max_width, widths.total, widths.buffer);
                widths = this.calculate_text_widths(ctx, letters, max_width);
                // If width is still too large, cut off text.
                // Add some wiggle room (1%) due to some inconsistencies with scaling the font size.
                while (widths.combined > max_width * 1.01) {
                    letters = letters.slice(0, letters.length - 1);
                    tspan.innerHTML = letters;
                    widths = this.calculate_text_widths(ctx, letters, max_width);
                    // See if resulting text can be scaled back up at all.
                    if (widths.combined <= max_width * 1.01) {
                        ctx.font = `${styles['font-weight']} ${styles['font-size']} ${styles['font-family']}`;
                        widths = this.calculate_text_widths(ctx, letters, max_width);
                        this.scale_font_size(ctx, styles, max_width, widths.total, widths.buffer);
                        widths = this.calculate_text_widths(ctx, letters, max_width);
                        break;
                    }
                }
            }

            if (update_svg) {
                const path_id = text.id + '_path';
                const start_point = `${path.start_point.x},${path.start_point.y}`;
                const control_point_1 = `${path.control_point_1.x},${path.control_point_1.y}`;
                const control_point_2 = `${path.control_point_2.x},${path.control_point_2.y}`;
                const end_point = `${path.end_point.x},${path.end_point.y}`;
                let path_data = `M ${start_point} C ${control_point_1} ${control_point_2} ${end_point}`;
                // Create SVG Path & TextPath.
                const svg_path = document.createElementNS('http://www.w3.org/2000/svg','path');
                svg_path.setAttribute('id', path_id);
                svg_path.setAttribute('stroke', `red`);
                svg_path.setAttribute('d', path_data);
                const svg_text_path = document.createElementNS('http://www.w3.org/2000/svg','textPath');
                svg_text_path.setAttribute('href', `#${path_id}`);
                svg_text_path.setAttribute('style', tspan.getAttribute('style'));
                svg_text_path.setAttribute('startOffset', "50%");
                svg_text_path.setAttribute('dominant-baseline', "middle");
                svg_text_path.setAttribute('text-anchor', "middle");
                svg_text_path.setAttribute('letter-spacing', "1.3");
                svg_text_path.innerHTML = tspan.innerHTML;
                text.appendChild(svg_path);
                text.appendChild(svg_text_path);
                text.removeChild(tspan);
                styles['font-size'] = `${this.current_font_size}px`;
                this.update_element_styles(svg_text_path, styles);
                // Don't draw text when creating svg.
                return;
            }

            let cur_width = 0;  
            ctx.textAlign = "center";
            // Calculate points along the curve to draw each letter.
            for (let i = 0; i < letters.length; i++) {
                const letter_width = ctx.measureText(letters[i]).width;
                cur_width += (widths.char + letter_width) / 2;
                const cur_point = this.get_cubic_bezier_point(
                    path.start_point, path.control_point_1, path.control_point_2, path.end_point,
                    (cur_width + widths.leading)/(widths.total + widths.buffer + widths.leading * 2)
                )
                const curve = Math.atan(cur_point.my / cur_point.mx);

                ctx.save();
                ctx.translate(cur_point.x, cur_point.y);
                ctx.rotate(curve);
                ctx.strokeText(letters[i], 0, 0);
                ctx.fillText(letters[i], 0, 0);
                
                cur_width += (widths.char + letter_width) / 2;
                ctx.restore();
            }
            // For debugging, draw the underlying curve.
            if (this.show_text_curve_lines) {
                ctx.beginPath();
                ctx.moveTo(path.start_point.x, path.start_point.y);
                ctx.bezierCurveTo(
                    path.control_point_1.x, 
                    path.control_point_1.y,
                    path.control_point_2.x,
                    path.control_point_2.y,
                    path.end_point.x,
                    path.end_point.y
                );
                const stroke_style = ctx.strokeStyle;
                ctx.strokeStyle = 'red';
                ctx.stroke();
                ctx.strokeStyle = stroke_style;
                ctx.strokeStyle = 'green';
                let ox = path.start_point.x;
                let oy = path.start_point.y;
                ctx.strokeRect(ox, oy, 1, 1);
                let length = 0;
                const points = 10;
                for(let i = 1; i <= points; i += 1) {
                    const cur_point = this.get_cubic_bezier_point(
                        path.start_point, path.control_point_1, path.control_point_2, path.end_point, i/points
                    )
                    
                    ctx.strokeRect(cur_point.x, cur_point.y, 1, 1);
                    const dx = ox - cur_point.x;
                    const dy = oy - cur_point.y;        
                    length += Math.sqrt(dx * dx + dy * dy);
                    ox = cur_point.x;
                    oy = cur_point.y;
                }
                ctx.strokeStyle = stroke_style;
            }

            return;
        }

        // Resize font if total_width > max_width.
        // Get initial width for use when resizing text later.
        // In order to measure the max width, the SVG must be set up so that each (non-curved) text field 
        // contains enough characters to fill the maximum allowable space.
        // Alternatively, it could be set manually to an attribute of the text (data-max-width).
            
        // If max width hasn't been calculated yet, set it to the data-max-width attribute.
        if (!text.dataset.maxWidth) {
            text.dataset.maxWidth = ctx.measureText(initial_text).width.toString();
        }

        // Check if line breaks are present and permitted.
        let text_lines = [letters];
        let longest_line_index = 0;
        if (text.hasAttribute('data-max-lines')) {
            const max_lines = parseInt(text.getAttribute('data-max-lines'));
            text_lines = letters.split('\n');
            while (text_lines.length > max_lines) {
                text_lines.pop();
            }
            let longest_line_width = 0;
            // Determine which line is longest for width scaling.
            for (let i = 0; i < text_lines.length; i++) {
                const line_width = ctx.measureText(text_lines[i]).width;
                if (line_width > longest_line_width) {
                    longest_line_width = line_width;
                    longest_line_index = i;
                }
            }
            letters = text_lines[longest_line_index];
        }
        
        const max_width = parseFloat(text.dataset.maxWidth);
        let total_width = ctx.measureText(letters).width;
        if (total_width > max_width) {
            this.scale_font_size(ctx, styles, max_width, total_width);
            // Truncate text if necessary.
            text_lines[longest_line_index] = this.truncate_text(ctx, letters, max_width, styles);
            for (let i = 0; i < text_lines.length; i++) {
                if (i === longest_line_index) {
                    continue;
                }
                text_lines[i] = this.truncate_text(ctx, text_lines[i], max_width, styles, false);
            }
        }

        let x = parseFloat(tspan.getAttribute('x'));
        let y = parseFloat(tspan.getAttribute('y'));
        let line_y = 0;

        for (let i = 0; i < text_lines.length; i++) {
            if (i > 0) {
                line_y += ctx.measureText(text_lines[i - 1]).fontBoundingBoxDescent + 
                    ctx.measureText(text_lines[i]).fontBoundingBoxAscent;
            }

            if (update_svg) {
                let text_element = tspan;
                // Create a new element for multi-line text.
                if (i > 0) {
                    text_element = tspan.cloneNode() as SVGTSpanElement;
                    tspan.parentElement.appendChild(text_element);
                    text_element.setAttribute('y', (y + line_y).toString());
                }
                // Update text and font size.
                text_element.textContent = text_lines[i];
                text_element.setAttribute('font-size', `${this.current_font_size}px`);
                styles['font-size'] = `${this.current_font_size}px`;
                this.update_element_styles(text_element, styles);
                // Don't draw text when creating svg.
                continue;
            }

            if (styles['stroke'] && styles['stroke'] != 'none') {
                ctx.strokeText(text_lines[i], x, y + line_y);
            }

            if (styles['fill'] != 'none') {
                ctx.fillText(text_lines[i], x, y + line_y);
            }

        }
    }

    /**
     * Applies the transform of the current element to the canvas.
     *
     * @param {SVGElement} element to be rendered.
     * @param {CanvasRenderingContext2D} ctx rendering context of canvas.
     * @returns {void}
     */
    set_transform(element: SVGGraphicsElement, ctx: CanvasRenderingContext2D, scale: boolean = true) {
        // Set Transform (position, width)
        let angle = 0;
        if (element.transform) {
            const transform_list = element.transform.baseVal as SVGTransformList;
            for (const transform of transform_list) {
                if (transform.angle) {
                    angle += transform.angle;
                    continue;
                }

                ctx.transform(
                    transform.matrix.a, transform.matrix.b, transform.matrix.c,
                    transform.matrix.d, transform.matrix.e, transform.matrix.f
                );
            }
        }
        
        if (scale) {
            const cur_transform = ctx.getTransform();
            ctx.setTransform(
                cur_transform.a * this.template_scale / this.window_scale,
                cur_transform.b,
                cur_transform.c,
                cur_transform.d * this.template_scale / this.window_scale,
                (cur_transform.e * this.template_scale + this.canvas_dimensions.offset_x) / this.window_scale,
                (cur_transform.f * this.template_scale + this.canvas_dimensions.offset_y) / this.window_scale 
            );
        }

        if (angle) {
            ctx.rotate(angle * Math.PI / 180);
        }
    }

    /**
     * Creates the clipping region from the current element's clip-path, if any.
     *
     * @param {SVGElement} element to be rendered.
     * @param {CanvasRenderingContext2D} ctx rendering context of canvas.
     * @returns {void}
     */
    create_clipping_region(element: SVGElement, ctx: CanvasRenderingContext2D) {
        // Get clipping path.
        let clip_path_id = element.getAttribute('clip-path');
        if (clip_path_id) {
            clip_path_id = clip_path_id.substring(5, clip_path_id.length - 1);
            const clip_path_element = this.template_svg.getElementById(clip_path_id)?.children[0];  
            if (!clip_path_element) {
                return;
            }

            const clip_path = this.element_to_path(clip_path_element as SVGElement);
            if (!clip_path) {
                return;
            }
            const cur_transform = ctx.getTransform();
            // Apply transform of element to be clipped as well as clipping element.
            this.set_transform(element as SVGGraphicsElement, ctx, false);
            this.set_transform(clip_path_element as SVGGraphicsElement, ctx);
            ctx.clip(clip_path);
            // Reset transformation.
            ctx.setTransform(cur_transform);
        }
    }
    
    /**
     * Applies the transform of the current element to the canvas.
     *
     * @param {SVGElement} element to be rendered.
     * @param {string} style_attribute the element's style attribute.
     * @param {object} defaults values to use if not present.
     * @returns {object} the element's styles with any provided defaults applied.
     */
    get_styles(element: SVGElement, style_attribute: string = '', defaults: {} = {}) {
        const styles = defaults;
        style_attribute ||= element.getAttribute('style');
        if (style_attribute) {
            style_attribute.split(';').forEach(style => {
                const style_pair = style.split(':'); 
                styles[style_pair[0]] = style_pair[1];
            })
        }
        // Check for any styling not defined in style attribute.
        for (const attribute of element.attributes) {
            // Skip non-style related attributes.
            if (this.ignored_styles.includes(attribute.name) || attribute.name.startsWith('data-')) {
                continue;
            }
            // Handle incompatible values.
            if (attribute.value === '#fff' || attribute.value === '#FFF' || attribute.value === '#FFFFFF') {
                attribute.value = '#ffffff';
            }

            styles[attribute.name] = attribute.value;
        }

        return styles;
    }

    /**
     * Converts and applies SVG filters, if any.
     *
     * @param {SVGElement} element to be rendered.
     * @param {CanvasRenderingContext2D} ctx rendering context of canvas.
     * @returns {void}
     */
    apply_filters(element: SVGElement, ctx: CanvasRenderingContext2D) {
        // Get filter.
        let filter_id = element.getAttribute('filter');
        if (filter_id) {
            filter_id = filter_id.substring(5, filter_id.length - 1);
            const filter_element = this.template_svg.getElementById(filter_id);
            if (!filter_element) {
                return;
            }

            for (const filter of filter_element.children) {
                const filter_type = filter.tagName;
                switch(filter_type) {
                    case 'feOffset':
                        ctx.shadowOffsetX = parseFloat(filter.getAttribute('dx')) * this.template_scale / this.window_scale;
                        ctx.shadowOffsetY = parseFloat(filter.getAttribute('dy')) * this.template_scale / this.window_scale;
                        break;
                    case 'feGaussianBlur':
                        ctx.shadowBlur = parseFloat(filter.getAttribute('stdDeviation'))
                            * this.template_scale / this.window_scale;
                        break;
                    case 'feFlood':
                        const alpha = (Math.floor(parseFloat(filter.getAttribute('flood-opacity')) * 255)).toString(16);
                        ctx.shadowColor = `${filter.getAttribute('flood-color')}${alpha}`;
                        break;
                    default:
                        break;
                }
            }
        }
    }

    /**
     * Checks if element's fill color is dictated by a question control.
     *
     * @param {object} styles of the current element.
     * @param {object} question_ids the ids of any questions which affect the element.
     * @returns {string} the fill color value.
     */
    get_fill_color(styles: object, question_ids: object) { 
        for (const question_id in question_ids) {
            if (question_ids[question_id] == 'fill') {
                // Get color from question control element.
                for (const child_config_id in this.configurator_v2.child_configs) {
                    const child_config = this.configurator_v2.child_configs[child_config_id];
                    for (const child_question of child_config.child_questions) {
                        if (child_question[1].data.questionId === parseInt(question_id)) {
                            const control_element = document.getElementById(
                                `selected_option_${child_question[0]}-${child_config.id}`
                            ) as HTMLInputElement;
                            if (control_element) {
                                return '#' + control_element.value;
                            }
                        }
                    }
                }
                break;
            }
        }

        return styles['fill'];
    }

    /**
     * Checks if element's text is dictated by a question control.
     *
     * @param {string} text of the element.
     * @param {object} question_ids the ids of any questions which affect the element.
     * @param {object} styles of the current element.
     * @returns {string} the text value.
     */
    get_text(text: string, question_ids: object, styles: object) { 
        for (const question_id in question_ids) {
            if (question_ids[question_id] == 'text' || question_ids[question_id] == 'compound') {
                // Get text from question control element.
                for (const child_config_id in this.configurator_v2.child_configs) {
                    const child_config = this.configurator_v2.child_configs[child_config_id];
                    for (const child_question of child_config.child_questions) {
                        let control_element = null;
                        if (child_question[1].data.id === parseInt(question_id)) {
                            if (question_ids[question_id] == 'compound') {
                                control_element = document.getElementsByName(
                                    `configuration[child_configurations][${child_config.parent_child_relation.id}][questions][${child_question[1].data.questionId}][compound_multiple_input][0][${question_ids['compound']}]`
                                )[0];
                            }
                            else {
                                const option_id = child_question[1].data.questionOptionRelations[0].questionOptionId;
                                control_element = document.getElementById(
                                    `question_${child_question[0]}_${child_config.parent_child_relation.id}_option_${option_id}`
                                ) as HTMLInputElement;
                            }
                            if (control_element) {
                                // Check if text element has specific case.
                                if (styles['text-transform']) {
                                    if (styles['text-transform'] === 'uppercase') {
                                        return control_element.value.toUpperCase();
                                    }

                                    if (styles['text-transform'] === 'lowercase') {
                                        return control_element.value.toLowerCase();
                                    }
                                }

                                return control_element.value;
                            }
                        }
                    }
                }
                break;
            }
        }

        return text;
    }

    /**
     * Calculates the widths of text using the canvas context.
     *
     * @param {CanvasRenderingContext2D} ctx rendering context of canvas.
     * @param {string} letters the text to be measured.
     * @param {number} max_width the maximum width for the text.
     * @returns {TextWidths} the width values of the text.
     */
    calculate_text_widths(ctx: CanvasRenderingContext2D, letters: string, max_width: number) : TextWidths {
        const total = ctx.measureText(letters).width;
        const char = ctx.measureText(" ").width / 2;
        const buffer = char * (letters.length - 1);
        const leading = (max_width - total - buffer) / 2;
        const combined = total + buffer;
        return {total, char, buffer, leading, combined};
    }
    
    /**
     * Truncates text to fit the specified max width;
     *
     * @param {CanvasRenderingContext2D} ctx rendering context of canvas.
     * @param {string} letters the text to be truncated.
     * @param {number} max_width the maximum width for the text.
     * @param {object} styles of the current element.
     * @param {boolean} scale_up whether to scale text up after truncation.
     * @returns {string} the truncated text.
     */
    truncate_text(
        ctx: CanvasRenderingContext2D,
        letters: string,
        max_width: number,
        styles: object,
        scale_up: boolean = true
    ) {
        let total_width = ctx.measureText(letters).width;
        // If width is too large, cut off text.
        // Add some wiggle room (1%) due to some inconsistencies with scaling the font size.
        while (total_width > max_width * 1.01) {
            letters = letters.slice(0, letters.length - 1);
            total_width = ctx.measureText(letters).width;
            // See if resulting text can be scaled back up at all.
            if (total_width <= max_width * 1.01) {
                if (scale_up) {
                    ctx.font = `${styles['font-weight']} ${styles['font-size']} ${styles['font-family']}`;
                    total_width = ctx.measureText(letters).width;
                    this.scale_font_size(ctx, styles, max_width, total_width);
                }
                break;
            }
        }

        return letters;
    }

    /**
     * Scales and sets the current font size.
     *
     * @param {CanvasRenderingContext2D} ctx rendering context of canvas.
     * @param {object} styles of the current element.
     * @param {number} max_width the maximum width for the text.
     * @param {number} total_width the total width of the text.
     * @param {number} buffer_space the buffer space of the text.
     * @param {number} min_scale the minimum scale for the text.
     * @returns {void}
     */
    scale_font_size(
        ctx: CanvasRenderingContext2D,
        styles: object,
        max_width: number,
        total_width: number,
        buffer_space: number = 0,
        min_scale: number = 0.5
    ) {
        const scale = Math.max(max_width / (total_width + buffer_space), min_scale);
        const font_size = parseFloat(styles['font-size'].substring(0, styles['font-size'].length - 2)) * scale;
        ctx.font = `${styles['font-weight']} ${font_size}px ${styles['font-family']}`;
        this.current_font_size = font_size;
    }

    /**
     * Returns a value along a curve using control values and the interval.
     *
     * @param {number} time the interval along the curve.
     * @param {number} a start value.
     * @param {number} b first control value.
     * @param {number} c second control value.
     * @param {number} d end value.
     * @returns {number} the value along the curve.
     */
    cubic_n(time: number, a: number, b: number, c: number, d: number) {
        const t2 = time*time;
        const t3 = t2*time;
        return a + (-a * 3 + time * (3 * a - a * time)) * time
        + (3 * b + time * (-6 * b + b * 3 * time)) * time
        + (c * 3 - c * 3 * time) * t2
        + d * t3;
    }

    /**
     * Returns a point along a bezier curve using control points and the interval.
     *
     * @param {any} start_point of the curve.
     * @param {any} control_point_1 first control point.
     * @param {any} control_point_2 second control point.
     * @param {any} end_point of the curve.
     * @param {any} time the interval from 0 - 1 along the curve.
     * @returns {object} the point along the curve.
     */
    get_cubic_bezier_point(start_point: any, control_point_1: any, control_point_2: any, end_point: any, time: number){
        const x = this.cubic_n(time,start_point.x,control_point_1.x,control_point_2.x,end_point.x);
        const y = this.cubic_n(time,start_point.y,control_point_1.y,control_point_2.y,end_point.y);
        const mx = (3 * (1 - time) * (1 - time) * (control_point_1.x - start_point.x)) + ((6 * (1 - time) * time) *
            (control_point_2.x - control_point_1.x)) + (3 * time * time * (end_point.x - control_point_2.x));
        const my = (3 * (1 - time) * (1 - time) * (control_point_1.y - start_point.y)) + ((6 * (1 - time) * time) *
            (control_point_2.y - control_point_1.y)) + (3 * time * time * (end_point.y - control_point_2.y));
        return({x:x,y:y,mx:mx,my:my});
    }

    /**
     * Returns the length of a bezier curve.
     *
     * @param {any} path of the bezier curve.
     * @param {number} points the number of points along the curve used to calculate the length.
     * @returns {number} the length of the curve.
     */
    get_bezier_length(path: any, points: number = 20) {
        let ox = path.start_point.x;
        let oy = path.start_point.y;
        let length = 0;
        for(let i = 1; i <= points; i += 1) {
            const cur_point = this.get_cubic_bezier_point(
                path.start_point, path.control_point_1, path.control_point_2, path.end_point, i/points
            )
            const dx = ox - cur_point.x;
            const dy = oy - cur_point.y;        
            length += Math.sqrt(dx * dx + dy * dy);
            ox = cur_point.x;
            oy = cur_point.y;
        }

        return length;
    }

    /**
     * Resets the position and scale of the photo.
     *
     * @returns {void}
     */
    reset_photo_position() {
        const photo_upload_area = this.get_photo_upload_area(this.photo_upload_element);
        const photo_scale_X = (photo_upload_area.width - 5) / this.photo.width;
        const photo_scale_Y = (photo_upload_area.height - 5) / this.photo.height;
        const photo_scale = Math.min(photo_scale_X, photo_scale_Y);
        this.photo_position.scale_x = photo_scale;
        this.photo_position.scale_y = photo_scale;
        this.photo_position.rotation = 0;
        this.photo_position.x = photo_upload_area.x - this.photo.width * photo_scale / 2;
        this.photo_position.y = photo_upload_area.y - this.photo.height * photo_scale / 2;
        if (this.use_bg_image) {
            this.photo_position.x += photo_upload_area.width / 2;
            this.photo_position.y += photo_upload_area.height / 2;
        }
    }

    /**
     * Returns the dimensions of the photo upload area.
     *
     * @param {Element} photo_upload_element the element which contains the photo.
     * @returns {object} the dimensions of the photo upload area.
     */
    get_photo_upload_area(photo_upload_element: Element) {
        const scaleX = this.canvas_dimensions.width /
            parseFloat(this.template_svg.documentElement.getAttribute('width'));
        const scaleY = this.canvas_dimensions.height /
            parseFloat(this.template_svg.documentElement.getAttribute('height'));
        const scale = Math.min(scaleX, scaleY);
        
        if (this.use_bg_image) {
            const upload_area = this.photo_upload_area.max_size - this.photo_upload_area.min_size;
            const rect_width = upload_area * this.print_size.width_scale;
            const rect_height = upload_area * this.print_size.height_scale;
            const rect_x = this.canvas_dimensions.width/2 - this.canvas_dimensions.offset_x - rect_width/2;
            const rect_y = this.canvas_dimensions.offset_y;

            return {
                width : rect_width,
                height : rect_height,
                x : rect_x,
                y : rect_y
            };
        }

        let width = 0;
        let height = 0;
        let x = 0;
        let y = 0;

        if (photo_upload_element.tagName === 'circle') {
            width = parseFloat(photo_upload_element.getAttribute('r')) * 2;
            height = width;
            x = parseFloat(photo_upload_element.getAttribute('cx'));
            y = parseFloat(photo_upload_element.getAttribute('cy'));
        }
        else if (photo_upload_element.tagName === 'polygon') {
            const points = photo_upload_element.getAttribute('points').split(' ');
            let min_x = parseFloat(points[0]);
            let max_x = parseFloat(points[0]);
            let min_y = parseFloat(points[1]);
            let max_y = parseFloat(points[1]);
            for (let i = 2; i < points.length; i +=2 ) {
                const point_x = parseFloat(points[i]);
                const point_y = parseFloat(points[i+1]);
                if (point_x < min_x) {
                    min_x = point_x;
                }
                else if (point_x > max_x) {
                    max_x = point_x;
                }
                if (point_y < min_y) {
                    min_y = point_y;
                }
                else if (point_y > max_y) {
                    max_y = point_y;
                }
            }
            width = max_x - min_x;
            height = max_y - min_y;
            x = min_x + width / 2;
            y = min_y + height / 2;
        }
        else {
            width = parseFloat(photo_upload_element.getAttribute('width'));
            height = parseFloat(photo_upload_element.getAttribute('height'));
            x = parseFloat(photo_upload_element.getAttribute('x'));
            y = parseFloat(photo_upload_element.getAttribute('y'));
        }

        const rect_width = width * scale;
        const rect_height = height * scale;
        const rect_x = x * scale + this.canvas_dimensions.offset_x;
        const rect_y = y * scale + this.canvas_dimensions.offset_y;

        return {
            width : rect_width,
            height : rect_height,
            x : rect_x,
            y : rect_y
        };
    }

    /**
     * Sets loading photo and removes placeholder image if present.
     *
     * @returns {void}
     */
    photo_upload_started() {
        this.loading_photo = true;
        if (this.photo_upload_placeholder) {
            this.template_svg.getElementById('photo-upload-placeholder').remove();
            this.photo_upload_placeholder = null;
        }
    }

    /**
     * Initializes the uploaded photo and updates the canvas.
     *
     * @param {string} img_url the url of the uploaded photo.
     * @returns {void}
     */
    photo_uploaded(img_url: string) {
        // Enable controls after first upload.
        if (!this.photo) {
            this.scale_slider.classList.remove('tv-disabled');
            this.elements.increase_scale_button.classList.remove('tv-disabled');
            this.elements.decrease_scale_button.classList.remove('tv-disabled');
            this.elements.rotate_button.classList.remove('tv-disabled');
        }

        this.photo_path = img_url;
        this.photo = new Image();
        this.photo.src = img_url;
        // Update cta.
        this.configurator_v2.show_add_to_cart_buttons();

        this.photo.onload = (event) => {
            this.loading_photo = false;
            // Set photo scale, position, and rotation.
            this.reset_photo_position();
            this.update_canvas();
        };
    }

    /**
     * Saves the svg output of the canvas.
     *
     * @returns {void}
     */
    create_svg() {
        const svg = this.template_svg.cloneNode(true) as Document;
        const images_to_encode = [];
        for (const image of svg.querySelectorAll('image')) {
            if (image.getAttribute('id') === 'background_image') {
                image.remove();
                continue;
            }
            const href = image.getAttributeNS('http://www.w3.org/1999/xlink','href');
            if (href && !href.startsWith('data')) {
                images_to_encode.push(new Promise<void>(function(resolve) {
                    const xhr = new XMLHttpRequest();
                    xhr.onload = function() {
                        const reader = new FileReader();
                        reader.onloadend = function() {
                            image.setAttributeNS('http://www.w3.org/1999/xlink','href', reader.result as string);
                            resolve();
                        }
                        reader.readAsDataURL(xhr.response);
                    }
                    xhr.open('GET', href);
                    xhr.responseType = 'blob';
                    xhr.send();
                }));
            }
        }

        Promise.all(images_to_encode).then(() => {
            const xml_serializer = new XMLSerializer();
            const svg_output = encodeURIComponent(xml_serializer.serializeToString(svg));
            $.ceAjax('request', `index.php?dispatch=tv_configurator_v2.save_canvas`, {
                method: 'POST',
                caching: true,
                hidden: true,
                data: {
                    configuration_id: this.configurator_v2.configuration_id,
                    svg: svg_output
                },
                callback: () => {}
            });
        });
    }

    /**
     * Converts styles to a string and sets it to an svg element's style attribute for svg output.
     *
     * @param {SVGElement} element to which the styles will be set.
     * @param {object} styles to set to the element.
     * @returns {void}
     */
    update_element_styles(element: SVGElement, styles: object) {
        let styles_string = JSON.stringify(styles);
        styles_string = styles_string.replaceAll('","', ';');
        styles_string = styles_string.replaceAll('"', '');
        styles_string = styles_string.slice(1, styles_string.length - 1);
        element.setAttribute('style', styles_string);
    }

}

